import initCommon from './common/common';
import queryRoots from './lib/query-roots';
import modules from './module-registry';
import sections from './section-registry';

const imports = new Map();
const instances = new WeakMap();

function reportError(name, error) {
    if (process.env.NODE_ENV !== 'production') {
        console.error(`[${name}] Failed to initialize`, error);
    }
}

function initOnce(element, name, load) {
    if (!instances.has(element)) instances.set(element, new Map());
    const states = instances.get(element);
    if (states.has(name)) return states.get(name);

    if (!imports.has(name)) {
        imports.set(name, Promise.resolve().then(load).catch((error) => {
            imports.delete(name);
            throw error;
        }));
    }

    const task = imports.get(name).then((module) => {
        const init = module.default || module.sectionCallBack;
        if (typeof init !== 'function') throw new TypeError(`Missing initializer: ${name}`);
        return init(element, (selector) => element.querySelector(selector),
            (selector) => Array.from(element.querySelectorAll(selector)));
    }).catch((error) => {
        states.delete(name);
        reportError(name, error);
    });
    states.set(name, task);
    return task;
}

function boot(root = document) {
    const context = root && typeof root.querySelectorAll === 'function' ? root : document;
    initCommon(context);
    const tasks = [];

    queryRoots('[data-module]', context).forEach((element) => {
        const names = new Set((element.dataset.module || '').split(/\s+/).filter(Boolean));
        names.forEach((name) => {
            const load = Object.hasOwn(modules, name) ? modules[name] : null;
            if (typeof load === 'function') tasks.push(initOnce(element, `module:${name}`, load));
        });
    });

    sections.forEach(({ selector, load }) => {
        queryRoots(selector, context).forEach((element) => {
            tasks.push(initOnce(element, `section:${selector}`, load));
        });
    });

    return Promise.all(tasks).then((results) => {
        document.dispatchEvent(new CustomEvent('ajust:ready', { detail: { root: context } }));
        return results;
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
} else {
    boot();
}

window.ajustBoot = boot;
